import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { CustomEventService } from '@shared/services/custom-event.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';
import DateUtil from '@shared/utils/date.util';

import {
  createFormControl,
  exceptLastValidator,
  uniqueArrayValidator,
  validateFormGroup
} from '@shared/utils/form.util';

import durations from '@shared/variables/durations.variable';
import { reportPeriod } from '@shared/variables/report.variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { differenceInCalendarDays } from 'date-fns';

@UntilDestroy()
@Component({
  selector: 'app-scheduled-reports-settings',
  templateUrl: './scheduled-reports-settings.component.html'
})
export class ScheduledReportsSettingsComponent implements OnInit, OnDestroy {

  @Input() loading: boolean;
  @Input() sendTestReportButtonLoading: boolean;
  @Input() title: string;
  @Input() mode: 'edit' | 'add';
  @Input() data;
  @Input() users: User.UserModel[];

  addReportForm: FormGroup;

  dateTimeFormat: string;
  dateFormat: string;
  dateText = durations.filter(item => item.key !== 'MONTHLY' && item.key !== 'YEARLY');
  periods = reportPeriod;

  formSubmitted = false;
  requiredControl: boolean;

  disabledDate = (current: Date): boolean => differenceInCalendarDays(current, new Date()) < 0;

  constructor(private customEventService: CustomEventService,
              private fb: FormBuilder,
              private globalSettingsService: GlobalSettingsService) { }

  ngOnInit(): void {
    this.dateFormat = this.globalSettingsService.dateFormat;
    this.users = this.users.filter(item => item.enabled);

    this.createForm();

    this.addReportForm.get('dateRangeType').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(item => {
      if (item === 'CUSTOM_DATE') {
        this.addReportForm.get('dateRangeFrom').setValidators(Validators.required);
      } else {
        this.addReportForm.get('dateRangeFrom').setValidators(null);
        this.addReportForm.get('dateRangeFrom').setValue(null);
      }
    });
  }

  createForm(): void {
    const { name = '', status = false, users = [], period = 'WEEKLY', periodFrom, customEmails, dateRangeType, dateRangeFrom } = this.data.report;

    this.addReportForm = this.fb.group({
      status: [this.data.smtpConfigured ? status : false, [Validators.required]],
      name: [name, [Validators.required]],
      period: [period, [Validators.required]],
      periodFrom: [periodFrom ? new Date(periodFrom) : new Date(), [Validators.required]],
      users: [users, []],
      customEmails: this.fb.array([], {validators: [uniqueArrayValidator, exceptLastValidator]}),
      dateRangeType: [dateRangeType, [Validators.required]],
      dateRangeFrom: [dateRangeFrom && new Date(dateRangeFrom), []]
    });

    if (customEmails && customEmails.length) {
      for ( const item of customEmails ) {
        this.addAccountItem(item);
      }
    } else {
      this.addAccountItem(null);
    }
  }

  get accounts(): FormArray {
    return this.addReportForm.get('customEmails') as FormArray;
  }

  validateAccounts(): boolean {
    return this.accounts.controls.every(control => {

      if (control.invalid || !control.value) {
        control.markAsDirty();

        if (!control.value) {
          control.setErrors({required: true});
        }
      }

      setTimeout(_ => {
        control.updateValueAndValidity();
      }, 350);

      // in order to add a new input all previous inputs should be filled
      return control.valid && control.value;
    });
  }

  addAccountItem(value?: string): void {

    if (this.validateAccounts()) {
      this.accounts.push(createFormControl(value, []));
    }
  }

  removeAccountItem(index): void {
    if (this.accounts.length > 1) {
      this.accounts.removeAt(index);
    }
  }

  submitForm(): void {
    this.formSubmitted = true;

    const formData = this.addReportForm.value;

    if (!formData.users.length && (formData.customEmails[0] === null || !formData.customEmails[0].length)) {
      this.requiredControl = true;
      return;
    }

    validateFormGroup(this.addReportForm);

    if (this.addReportForm.invalid) {
      return;
    }

    formData.periodFrom = formData.periodFrom.getTime();
    formData.dateRangeFrom = formData.dateRangeFrom ? formData.dateRangeFrom.getTime() : null;
    formData.customEmails = formData.customEmails.filter(item => item);

    this.customEventService.announceEvent(this.mode === 'add' ? ActionTypesEnum.CREATE_REPORT : ActionTypesEnum.UPDATE_REPORT, formData);
  }

  sendReport(): void {
    this.customEventService.announceEvent(ActionTypesEnum.SEND_TEST_REPORT);
  };

  resetForm(): void {
    this.createForm();
  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
  }
}
